/**
 * AOL-Instant-Messenger-style IM sounds for the private-chat ("IM mode")
 * windows: a door-OPEN swing when a buddy window opens, a door-CLOSE thud when
 * it closes, and a short message blip when an IM arrives.
 *
 * Like notifySound.js these are SYNTHESIZED with the Web Audio API — no asset
 * files to bundle, works offline, and one lazily-created AudioContext is reused.
 * Browsers gate audio until a user gesture, so the very first sound after a
 * fresh load may be silent; any later one (post-interaction) plays. Every
 * function is best-effort and never throws — a sound must never break the
 * message pipeline.
 *
 * `volume` is 0-100 (reuses the user's notification-volume preference); 0 is
 * silent. The classic AIM trio is recognizable by shape, not sample-accuracy:
 *   - open:    a quick upward "creak" (door swinging open)
 *   - close:   a short downward thud (door shutting)
 *   - message: a soft two-tone blip
 */

let audioCtx = null;

// -----------------------------------------------------------------------------
// Classic AIM sample playback. The retro ("classic") IM skin uses the real
// AOL sound effects (dropped in chat/public/sounds/) instead of the synthesized
// tones below. These are short MP3s played via <audio>; we keep one reusable
// Audio element per sample and clone-free rewind so rapid messages re-trigger.
// Best-effort and never throws — a sound must never break the message pipeline.
// -----------------------------------------------------------------------------
const SAMPLE_SRC = {
  send: '/sounds/SendMsg.mp3',
  recv: '/sounds/RecvMsg.mp3',
};
const sampleEls = {};

function getSample(name) {
  if (typeof Audio === 'undefined') return null;
  const src = SAMPLE_SRC[name];
  if (!src) return null;
  if (!sampleEls[name]) {
    try {
      const el = new Audio(src);
      el.preload = 'auto';
      sampleEls[name] = el;
    } catch {
      return null;
    }
  }
  return sampleEls[name];
}

// Play one of the AIM MP3 samples ('send' | 'recv') at the given 0-100 volume.
// Rewinds to the start so back-to-back messages each re-trigger. Never throws.
function playSample(name, volume) {
  const el = getSample(name);
  if (!el) return;
  const v = Math.min(100, Math.max(0, Number(volume)));
  if (!Number.isFinite(v) || v <= 0) return;
  try {
    el.volume = v / 100;
    el.currentTime = 0;
    const p = el.play();
    if (p && p.catch) p.catch(() => {}); // autoplay gate before first gesture
  } catch {
    /* never let an IM sound break the message pipeline */
  }
}

function getCtx() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctx();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

// Peak gain at volume 100. Kept a touch under the chime's peak so door sounds
// don't startle; scaled linearly by the caller's 0-100 volume.
const PEAK_GAIN = 0.16;

// Resolve a usable context + a normalized peak for `volume`, or null to skip.
function prep(volume) {
  const ctx = getCtx();
  if (!ctx) return null;
  if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
  const v = Math.min(100, Math.max(0, Number(volume)));
  if (!Number.isFinite(v) || v <= 0) return null;
  // exponentialRampToValueAtTime can't target 0, so peaks stay > 0.
  const peak = Math.max(0.0002, PEAK_GAIN * (v / 100));
  return { ctx, peak };
}

// One enveloped tone. `slide` (Hz) bends the frequency over the note for the
// door "swing"; `type` picks the oscillator timbre.
function tone(ctx, peak, { freq, at, dur, slide = 0, type = 'sine' }) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  const t0 = ctx.currentTime + at;
  osc.frequency.setValueAtTime(freq, t0);
  if (slide) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(20, freq + slide),
      t0 + dur,
    );
  }
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// Door opening — a short rising creak (low triangle sliding up).
function playOpen(volume) {
  const p = prep(volume);
  if (!p) return;
  tone(p.ctx, p.peak, { freq: 220, at: 0, dur: 0.18, slide: 320, type: 'triangle' });
  tone(p.ctx, p.peak * 0.5, { freq: 660, at: 0.05, dur: 0.12, type: 'sine' });
}

// Door closing — a short falling thud (sliding down, slightly softer).
function playClose(volume) {
  const p = prep(volume);
  if (!p) return;
  tone(p.ctx, p.peak * 0.9, { freq: 520, at: 0, dur: 0.16, slide: -360, type: 'triangle' });
  tone(p.ctx, p.peak * 0.45, { freq: 160, at: 0.04, dur: 0.16, type: 'sine' });
}

// Incoming IM — a soft two-tone blip (B5 -> E6), gentler than the @mention chime.
function playMessage(volume) {
  const p = prep(volume);
  if (!p) return;
  tone(p.ctx, p.peak, { freq: 987.77, at: 0, dur: 0.16, type: 'sine' });
  tone(p.ctx, p.peak, { freq: 1318.51, at: 0.08, dur: 0.18, type: 'sine' });
}

/**
 * Play an IM sound by name. Unknown names are no-ops. Never throws.
 *   open/close/message : synthesized Web-Audio tones (default skin).
 *   imsend/imrecv      : the real AOL MP3 samples (classic/AIM skin).
 * @param {'open'|'close'|'message'|'imsend'|'imrecv'} kind
 * @param {number} [volume] 0-100; defaults to full volume
 */
export function playImSound(kind, volume = 100) {
  try {
    switch (kind) {
      case 'open':
        playOpen(volume);
        break;
      case 'close':
        playClose(volume);
        break;
      case 'message':
        playMessage(volume);
        break;
      case 'imsend':
        playSample('send', volume);
        break;
      case 'imrecv':
        playSample('recv', volume);
        break;
      default:
        break;
    }
  } catch {
    /* never let an IM sound break the message pipeline */
  }
}
